import styled from "@emotion/styled";
import { useState, useEffect, useMemo, useRef } from "react";
import { useAppStore } from "@/store";
import { RecordingStatus } from "@/store/audio-capture/AppStoreAudioCaptureStore";
import { observer } from "mobx-react-lite";
import { useTheme } from "@emotion/react";
import {
  bytesToFloat32,
  createWaveformProcessor,
  WaveformProcessor,
  WaveformProcessorOptions,
} from "@/store/audio-capture/audio";

enum AudioCaptureWaveFormVariant {
  Default = "default",
  OnAccent = "on-accent",
}

interface AudioCaptureWaveFormProps {
  className?: string;
  variant?: AudioCaptureWaveFormVariant;
  visualizer?: TimeDomainDataSource;
  isActive?: boolean;
  processorFactory?: () => WaveformProcessor;
  processorOptions?: WaveformProcessorOptions;
}

export interface TimeDomainDataSource {
  fftSize: number;
  getFloatTimeDomainData?(dataArray: Float32Array): void;
  getByteTimeDomainData?(dataArray: Uint8Array): void;
}

export const AudioCaptureWaveForm = observer(
  ({
    className,
    variant = AudioCaptureWaveFormVariant.Default,
    visualizer: visualizerOverride,
    isActive: isActiveOverride,
    processorFactory,
    processorOptions,
  }: AudioCaptureWaveFormProps) => {
    const { store } = useAppStore();
    const theme = useTheme();
    const barsColor =
      variant === AudioCaptureWaveFormVariant.Default
        ? theme.colors.icon.emphasis
        : theme.colors.icon.onAccent;
    const [waveFormBarHeights, setWaveFormBarHeights] = useState<number[]>();
    const { amplitudeScale, barCount, minVisibleHeight, smoothingFactor } = processorOptions ?? {};

    const processor = useMemo(() => {
      if (processorFactory) return processorFactory();
      return createWaveformProcessor({
        amplitudeScale,
        barCount,
        minVisibleHeight,
        smoothingFactor,
      });
    }, [processorFactory, amplitudeScale, barCount, minVisibleHeight, smoothingFactor]);
    const processorRef = useRef<WaveformProcessor>(processor);

    useEffect(() => {
      processorRef.current = processor;
      processorRef.current.reset();
    }, [processor]);

    useEffect(() => {
      const visualizer: TimeDomainDataSource | undefined =
        visualizerOverride ?? (store.audioCapture.visualizer as TimeDomainDataSource | undefined);
      if (!visualizer) {
        processorRef.current.reset();
        setWaveFormBarHeights(undefined);
        return;
      }
      const floatDomainData = new Float32Array(visualizer.fftSize);
      const byteDomainData = new Uint8Array(visualizer.fftSize);
      let frameCounter = 0;
      let animationFrameId: number;

      const animate = () => {
        animationFrameId = requestAnimationFrame(animate);

        // Only update every 12th frame
        frameCounter++;
        if (frameCounter % 12 !== 0) return;

        const isRecording =
          typeof isActiveOverride === "boolean"
            ? isActiveOverride
            : store.audioCapture.status === RecordingStatus.RECORDING;

        if (!isRecording) {
          processorRef.current.reset();
          setWaveFormBarHeights(undefined);
          return;
        }

        if (visualizer.getFloatTimeDomainData) {
          visualizer.getFloatTimeDomainData(floatDomainData);
        } else if (visualizer.getByteTimeDomainData) {
          visualizer.getByteTimeDomainData(byteDomainData);
          const converted = bytesToFloat32(byteDomainData);
          floatDomainData.set(converted);
        } else {
          return;
        }

        const heights = processorRef.current.process(floatDomainData);
        setWaveFormBarHeights(heights);
      };

      animate();
      return () => {
        cancelAnimationFrame(animationFrameId);
        processorRef.current.reset();
        setWaveFormBarHeights(undefined);
      };
    }, [
      isActiveOverride,
      processor,
      store.audioCapture.status,
      store.audioCapture.visualizer,
      visualizerOverride,
    ]);

    return (
      <WaveFormContainer className={className}>
        {waveFormBarHeights?.map((height, index) => (
          <WaveFormBar
            key={index}
            style={{ height: `${height * 100}%`, backgroundColor: barsColor }}
          />
        ))}
        {!waveFormBarHeights && (
          <>
            <WaveFormBar $rounded style={{ height: `32%`, backgroundColor: barsColor }} />
            <WaveFormBar $rounded style={{ height: `32%`, backgroundColor: barsColor }} />
            <WaveFormBar $rounded style={{ height: `32%`, backgroundColor: barsColor }} />
          </>
        )}
      </WaveFormContainer>
    );
  }
);

const WaveFormContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: 0 1px;
  width: 16px;
  height: 16px;
  gap: 1px;
`;

const WaveFormBar = styled.div<{ $rounded?: boolean }>`
  width: 4px;
  transition: height 0.1s ease-in-out;
  border-radius: ${({ theme, $rounded }) =>
    $rounded ? theme.borderRadius.max : theme.borderRadius.small};
`;
